import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Sparkles, ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Wizard obligatorio de bienvenida para la modelo en su primer login.
 * NO permite cerrar (no ESC, no click-outside, no botón X).
 * Los campos vienen pre-cargados con lo que el admin ya escribió — la modelo
 * los complementa o edita a su gusto. NUNCA se piden precios ni % de agencia.
 */
const STEPS = [
  {
    key: "biografia",
    label: "Cuéntanos sobre ti",
    hint: "Tu historia, edad, personalidad, gustos, estilo de vida...",
    placeholder:
      "Ej: Soy Adonay, tengo 24 años, latina, apasionada por el fitness y la moda. Me encanta viajar y conocer gente nueva...",
    multiline: true,
  },
  {
    key: "personalidad",
    label: "¿Cómo describirías tu personalidad?",
    hint: "En pocas palabras: divertida, tímida, dominante, cariñosa, etc.",
    placeholder: "Ej: Alegre, coqueta, con mucho sentido del humor",
    multiline: false,
  },
  {
    key: "usuarios_potenciales",
    label: "¿Cómo es tu público ideal?",
    hint: "Tipo de fans que más interactúan contigo — edades, países, intereses.",
    placeholder:
      "Ej: Hombres entre 25-45 años, USA y Europa, les gusta el fitness y las latinas...",
    multiline: true,
  },
  {
    key: "metodologia_chat",
    label: "¿Cómo prefieres que se lleven tus conversaciones?",
    hint: "Estrategias de conversación, temas favoritos, qué NO quieres.",
    placeholder:
      "Ej: Empezar con preguntas casuales, usar mucho el sentido del humor, no hablar de política ni religión...",
    multiline: true,
  },
  {
    key: "expresion_chat",
    label: "¿Cómo te expresas normalmente en el chat?",
    hint: "Tono, emojis, palabras características, forma de escribir.",
    placeholder:
      "Ej: Uso muchos emojis 😍💕, escribo con signos, palabras favoritas: 'baby', 'amor', 'papi'...",
    multiline: true,
  },
  {
    key: "social_links",
    label: "Tus redes sociales (opcional)",
    hint: "Nos ayuda a promocionarte mejor. Deja en blanco lo que no uses.",
    isSocial: true,
  },
];

const SOCIAL_PLATFORMS = [
  { key: "instagram", label: "Instagram", placeholder: "@tuUsuario o URL" },
  { key: "tiktok", label: "TikTok", placeholder: "@tuUsuario" },
  { key: "twitter", label: "Twitter / X", placeholder: "@tuUsuario" },
  { key: "onlyfans", label: "OnlyFans", placeholder: "onlyfans.com/tuUsuario" },
];

export default function ModeloOnboardingWizard({ initialData, modelName, onComplete }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    biografia: initialData?.biografia || "",
    personalidad: initialData?.personalidad || "",
    usuarios_potenciales: initialData?.usuarios_potenciales || "",
    metodologia_chat: initialData?.metodologia_chat || "",
    expresion_chat: initialData?.expresion_chat || "",
    social_links: initialData?.social_links || {},
  });

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;

  // Validación mínima: solo la biografía es realmente obligatoria en el paso 1
  const canAdvance = () => {
    if (current.key === "biografia") {
      return (form.biografia || "").trim().length >= 15;
    }
    if (current.isSocial) return true;
    return true; // el resto son opcionales para no bloquear a la modelo
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/models/me/onboarding`, form, { withCredentials: true });
      toast.success("¡Bienvenida! Tu perfil quedó guardado 💕");
      onComplete && onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar tu perfil");
    } finally {
      setSaving(false);
    }
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setSocial = (platform, value) =>
    setForm((prev) => ({
      ...prev,
      social_links: { ...(prev.social_links || {}), [platform]: value },
    }));

  return (
    <div
      data-testid="modelo-onboarding-wizard"
      className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      // No permitimos cerrar con click en el fondo
      onClick={(e) => e.stopPropagation()}
    >
      <div className="w-full max-w-2xl bg-gradient-to-br from-zinc-900 to-zinc-950 border border-pink-500/20 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-white/5 bg-gradient-to-r from-pink-500/10 to-purple-500/10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-white text-2xl font-bold">
                ¡Bienvenida{modelName ? `, ${modelName}` : ""}!
              </h2>
              <p className="text-sm text-gray-400">
                Cuéntanos un poco más sobre ti para armar tu perfil
              </p>
            </div>
          </div>
          {/* Progress bar */}
          <div className="flex gap-1.5 mt-4">
            {STEPS.map((_, idx) => (
              <div
                key={idx}
                className={`h-1.5 flex-1 rounded-full transition-all ${
                  idx <= step ? "bg-gradient-to-r from-pink-500 to-purple-500" : "bg-white/10"
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Paso {step + 1} de {STEPS.length}
          </p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 min-h-[280px]">
          <div>
            <Label className="text-white text-lg font-semibold">{current.label}</Label>
            {current.hint && (
              <p className="text-sm text-gray-400 mt-1 mb-3">{current.hint}</p>
            )}
          </div>

          {current.isSocial ? (
            <div className="space-y-3">
              {SOCIAL_PLATFORMS.map((sp) => (
                <div key={sp.key}>
                  <Label className="text-gray-300 text-sm">{sp.label}</Label>
                  <Input
                    data-testid={`onboarding-social-${sp.key}`}
                    value={form.social_links[sp.key] || ""}
                    onChange={(e) => setSocial(sp.key, e.target.value)}
                    placeholder={sp.placeholder}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
              ))}
            </div>
          ) : current.multiline ? (
            <textarea
              data-testid={`onboarding-field-${current.key}`}
              value={form[current.key] || ""}
              onChange={(e) => setField(current.key, e.target.value)}
              placeholder={current.placeholder}
              rows={6}
              className="w-full p-3 bg-zinc-800 border border-zinc-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-500 resize-none"
              autoFocus
            />
          ) : (
            <Input
              data-testid={`onboarding-field-${current.key}`}
              value={form[current.key] || ""}
              onChange={(e) => setField(current.key, e.target.value)}
              placeholder={current.placeholder}
              className="bg-zinc-800 border-zinc-700 text-white"
              autoFocus
            />
          )}

          {current.key === "biografia" && (form.biografia || "").trim().length < 15 && (
            <p className="text-xs text-orange-400">
              ℹ️ Escribe al menos 15 caracteres para continuar
            </p>
          )}
        </div>

        {/* Footer / Actions */}
        <div className="p-6 border-t border-white/5 flex items-center justify-between gap-3 bg-black/40">
          <Button
            data-testid="onboarding-prev-btn"
            variant="outline"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={isFirst || saving}
            className="border-zinc-700 text-gray-300 hover:bg-zinc-800"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Atrás
          </Button>

          <div className="text-xs text-gray-500 hidden sm:block">
            🔒 No puedes saltar este paso — es la única vez
          </div>

          {isLast ? (
            <Button
              data-testid="onboarding-finish-btn"
              onClick={handleSubmit}
              disabled={saving}
              className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold"
            >
              {saving ? (
                "Guardando..."
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />
                  Finalizar
                </>
              )}
            </Button>
          ) : (
            <Button
              data-testid="onboarding-next-btn"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canAdvance() || saving}
              className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold"
            >
              Siguiente
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
